import {
  AvailablePlansTypes,
  defaultAgencyPlan,
  defaultAutomatePlan,
  defaultCorePlan,
  defaultCreatePlan,
  defaultEnterprisePlan,
  defaultFreePlan,
  defaultLitePlan,
  defaultOldAgencyPlan,
  defaultProfessionalPlan,
  PLANS
} from '@constants/plans'
import { formatNumericBasedOnLanguage } from '@helpers/data'
import { Icon, Paragraph, Select, Spreader, Tooltip } from '@ui-kit'
import { Dispatch, SetStateAction } from 'react'
import { Trans } from 'react-i18next'

const {
  PROFESSIONAL_100K_23,
  PROFESSIONAL_200K_23,
  PROFESSIONAL_1M_23,
  PROFESSIONAL_500K_23,
  AGENCY_100K_23,
  AGENCY_200K_23,
  AGENCY_500K_23,
  AGENCY_1M_23
} = PLANS

const visitsLabel = (visits: number) => (
  <Paragraph size={16} lineHeight='normal'>
    <Trans
      i18nKey='checkout.plans.plan.limits.visits'
      values={{ count: formatNumericBasedOnLanguage(visits) }}
    />
  </Paragraph>
)

const visitsLabelUnlimited = (
  <Paragraph size={16}>
    <Trans i18nKey='checkout.plans.plan.limits.visits.unlimited' />
  </Paragraph>
)

const domainsLabel = (domains: number) => (
  <Tooltip
    content={<Trans i18nKey='checkout.plans.plan.limits.domains.tooltip' />}
  >
    <Paragraph size={16}>
      <Trans
        i18nKey='checkout.plans.plan.limits.domains'
        count={domains}
        values={{ formattedCount: formatNumericBasedOnLanguage(domains) }}
      />

      <Spreader spread={5} />

      <Icon icon='icon-help_outline' size={14} color='neutral-4' />
    </Paragraph>
  </Tooltip>
)

const landingPagesLabel = (landingPages: number) => (
  <Paragraph size={16}>
    <Trans
      i18nKey='checkout.plans.plan.limits.landing.pages'
      count={landingPages}
      values={{ formattedCount: formatNumericBasedOnLanguage(landingPages) }}
    />
  </Paragraph>
)

const landingPagesLabelUnlimited = (
  <Paragraph size={16}>
    <Trans i18nKey='checkout.plans.plan.limits.landing.pages.unlimited' />
  </Paragraph>
)

const conversionsLabel = (conversions: number) => (
  <Paragraph size={16}>
    <Trans
      i18nKey='checkout.plans.plan.limits.conversions'
      values={{ count: formatNumericBasedOnLanguage(conversions) }}
    />
  </Paragraph>
)

const conversionsLabelUnlimited = (
  <Paragraph size={16}>
    <Trans i18nKey='compare.plans.description.limits.unlimited.conversion' />
  </Paragraph>
)

export const visits = {
  [defaultFreePlan]: 100,
  [defaultLitePlan]: 5000,
  [defaultProfessionalPlan]: 50000,
  [defaultAgencyPlan]: 50000,
  [defaultCorePlan]: 1000,
  [defaultCreatePlan]: 20000,
  [defaultAutomatePlan]: 40000,
  [defaultOldAgencyPlan]: 50000,
  [defaultEnterprisePlan]: 0, // Unlimited
  [PROFESSIONAL_100K_23]: 100000,
  [PROFESSIONAL_200K_23]: 200000,
  [PROFESSIONAL_500K_23]: 500000,
  [PROFESSIONAL_1M_23]: 1000000,
  [AGENCY_100K_23]: 100000,
  [AGENCY_200K_23]: 200000,
  [AGENCY_500K_23]: 500000,
  [AGENCY_1M_23]: 1000000
}

export const useGetPlanLimits = ({
  recordKey,
  setPlanTypeName
}: {
  recordKey: AvailablePlansTypes
  setPlanTypeName?: Dispatch<SetStateAction<AvailablePlansTypes>>
}) => {
  const PROFESSIONAL_VARIANTS = [
    {
      value: defaultProfessionalPlan,
      label: visitsLabel(visits[defaultProfessionalPlan])
    },
    {
      value: PROFESSIONAL_100K_23,
      label: visitsLabel(visits[PROFESSIONAL_100K_23])
    },
    {
      value: PROFESSIONAL_200K_23,
      label: visitsLabel(visits[PROFESSIONAL_200K_23])
    },
    {
      value: PROFESSIONAL_500K_23,
      label: visitsLabel(visits[PROFESSIONAL_500K_23])
    },
    {
      value: PROFESSIONAL_1M_23,
      label: visitsLabel(visits[PROFESSIONAL_1M_23])
    }
  ]

  const AGENCY_VARIANTS = [
    { value: defaultAgencyPlan, label: visitsLabel(visits[defaultAgencyPlan]) },
    { value: AGENCY_100K_23, label: visitsLabel(visits[AGENCY_100K_23]) },
    { value: AGENCY_200K_23, label: visitsLabel(visits[AGENCY_200K_23]) },
    { value: AGENCY_500K_23, label: visitsLabel(visits[AGENCY_500K_23]) },
    { value: AGENCY_1M_23, label: visitsLabel(visits[AGENCY_1M_23]) }
  ]

  const limits = {
    [defaultFreePlan]: {
      visits: visitsLabel(visits[defaultFreePlan]),
      domains: domainsLabel(1),
      landingPages: landingPagesLabel(5),
      conversions: conversionsLabel(100)
    },
    [defaultLitePlan]: {
      visits: visitsLabel(visits[defaultLitePlan]),
      domains: domainsLabel(1),
      landingPages: landingPagesLabel(10),
      conversions: conversionsLabelUnlimited
    },
    [defaultProfessionalPlan]: {
      visits: (
        <Select
          onChange={setPlanTypeName}
          options={PROFESSIONAL_VARIANTS}
          value={recordKey}
          overflowStyle={{}}
          data-testid='visits-select'
        />
      ),
      domains: domainsLabel(10),
      landingPages: landingPagesLabelUnlimited,
      conversions: conversionsLabelUnlimited
    },
    [defaultAgencyPlan]: {
      visits: (
        <Select
          onChange={setPlanTypeName}
          options={AGENCY_VARIANTS}
          value={recordKey}
          overflowStyle={{}}
          data-testid='visits-select'
        />
      ),
      domains: domainsLabel(20),
      landingPages: landingPagesLabelUnlimited,
      conversions: conversionsLabelUnlimited
    },
    [defaultCorePlan]: {
      visits: visitsLabel(visits[defaultCorePlan]),
      domains: domainsLabel(1),
      landingPages: landingPagesLabelUnlimited,
      conversions: conversionsLabelUnlimited
    },
    [defaultCreatePlan]: {
      visits: visitsLabel(visits[defaultCreatePlan]),
      domains: domainsLabel(2),
      landingPages: landingPagesLabelUnlimited,
      conversions: conversionsLabelUnlimited
    },
    [defaultAutomatePlan]: {
      visits: visitsLabel(visits[defaultAutomatePlan]),
      domains: domainsLabel(4),
      landingPages: landingPagesLabelUnlimited,
      conversions: conversionsLabelUnlimited
    },
    [defaultOldAgencyPlan]: {
      visits: visitsLabel(visits[defaultOldAgencyPlan]),
      domains: domainsLabel(5),
      landingPages: landingPagesLabelUnlimited,
      conversions: conversionsLabelUnlimited
    },
    [defaultEnterprisePlan]: {
      visits: visitsLabelUnlimited,
      domains: domainsLabel(100),
      landingPages: landingPagesLabelUnlimited,
      conversions: conversionsLabelUnlimited
    }
  }

  if (PROFESSIONAL_VARIANTS.some(variant => variant.value === recordKey)) {
    return limits[defaultProfessionalPlan]
  }

  if (AGENCY_VARIANTS.some(variant => variant.value === recordKey)) {
    return limits[defaultAgencyPlan]
  }

  return limits[recordKey as keyof typeof limits]
}
